import { format, subMonths } from 'date-fns';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';
import numberUtils from 'app/shared/utils/numberUtils';

const { USER_TYPES } = UserPermissionsConstants;
const nonNucleusRoles = new Set([USER_TYPES.MULTIFAMILY_CUSTOMER, USER_TYPES.ROCKETSHIP_CUSTOMER]);
const nonRocketshipRoles = new Set([USER_TYPES.NUCLEUS_CUSTOMER, USER_TYPES.MULTIFAMILY_CUSTOMER]);

const billingUtils = {
    formatZuoraRefresh: (refreshTime) => {
        if (!refreshTime) return null;
        const date = new Date(refreshTime);
        return format(date, 'MM/DD/YY HH:MMa');
    },
    isOnlyNucleusUser: (user) => {
        const { roles } = user;
        return !roles.some(role => nonNucleusRoles.has(role));
    },
    isOnlyRocketshipUser: (user) => {
        const { roles } = user;
        let rocketshipRoleFound = false;
        // eslint-disable-next-line guard-for-in
        for (const i in roles) {
            const role = roles[i];
            if (nonRocketshipRoles.has(role)) {
                return false;
            }
            rocketshipRoleFound = rocketshipRoleFound || role === USER_TYPES.ROCKETSHIP_CUSTOMER;
        }
        return rocketshipRoleFound;
    },
    calculateServicePeriod: (invoiceItems) => {
        let servicePeriod = '';
        if (invoiceItems && invoiceItems.length !== 0) {
            const currMinStart = Math.min(...invoiceItems
                .map((element => new Date(element.serviceStartDate).getTime())));
            const currMaxEnd = Math.max(...invoiceItems
                .map((element => new Date(element.serviceEndDate).getTime())));
            servicePeriod = `${new Date(currMinStart).toLocaleDateString()} -
            ${new Date(currMaxEnd).toLocaleDateString()}`;
        }
        return servicePeriod;
    },
    validateInvoicePayment: (invoice, selectedCreditCardId, useBalance, customAmount) => {
        if (!selectedCreditCardId) {
            return 'No credit card selected.';
        }
        if (!useBalance && customAmount > invoice.balance) {
            return 'You cannot make a payment greater than the invoice balance.';
        }
        return null;
    },
    getNextAutoPaymentDate: () => {
        const now = new Date();
        const nextBillingDate = new Date();
        nextBillingDate.setDate(15);
        if (now.getDate() > 15) {
            nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
        }
        return format(nextBillingDate, 'MM/DD/YYYY');
    },
    getCreditCardLastDigits: (maskNumber = '') => maskNumber.slice(maskNumber.length - 4, maskNumber.length),
    formatPaymentMethod: (paymentMethod) => {
        if (paymentMethod.paymentType === 'CreditCard') {
            return `${paymentMethod.brand} *${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)}`;
        }
        if (paymentMethod.paymentType === 'ACH') {
            return `${paymentMethod.accountType} *${billingUtils.getCreditCardLastDigits(paymentMethod.maskNumber)}`;
        }
        return '-';
    },
    invoiceInAutopayRange: (invoice, date = new Date()) => {
        const chargeDay = new Date(date.getFullYear(), date.getMonth(), 15);
        const lastMonth = subMonths(date, 1);
        lastMonth.setDate(1);
        if (date > chargeDay) return false;
        if (new Date(invoice.invoiceDate) >= lastMonth) {
            return true;
        }
        return false;
    },
    formatDiscountRate(value) {
        const { discountPercent } = value;
        if (!discountPercent) return null;
        const total = value.chargeAmount / value.quantity;
        const discount = (100 - discountPercent) / 100;
        const originalValue = total / discount;
        const formattedOriginalValue = numberUtils.asCurrencyWithDefault(originalValue, '-');
        return `(${formattedOriginalValue} with ${discountPercent}% discount)`;
    },
    formatDiscountAmount(value) {
        const { discountPercent, chargeAmount } = value;
        if (!discountPercent) return null;
        const discount = (100 - discountPercent) / 100;
        const originalValue = chargeAmount / discount;
        const discountedAmount = originalValue - chargeAmount;
        const formattedDiscountAmount = numberUtils.asCurrencyWithDefault(discountedAmount, '-');
        return `(${formattedDiscountAmount} discounted)`;
    },
};

export default billingUtils;
